import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';
import { FlipSourcesSection } from './FlipSourcesSection';
import { GenresSection } from './GenresSection';
import { MatchesSection } from './MatchesSection';
import { OverviewSection } from './OverviewSection';
import { PacksSection } from './PacksSection';
import { PlaylistsSection } from './PlaylistsSection';
import { ReportsSection } from './ReportsSection';
import { RulesSection } from './RulesSection';
import { SupportSection } from './SupportSection';
import { TournamentsSection } from './TournamentsSection';
import { UsersSection } from './UsersSection';

const TAB_IDS = [
  'overview',
  'users',
  'genres',
  'packs',
  'flip-sources',
  'matches',
  'rules',
  'tournaments',
  'playlists',
  'reports',
  'support',
] as const;
type TabId = (typeof TAB_IDS)[number];

const searchSchema = z.object({
  tab: z.enum(TAB_IDS).optional().default('overview'),
});

export const Route = createFileRoute('/admin/')({
  head: () =>
    buildHead({
      title: 'Admin',
      description: 'Internal admin panel.',
      path: '/admin',
      noindex: true,
    }),
  validateSearch: (search) => searchSchema.parse(search),
  component: AdminPanel,
});

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'users', label: 'Users' },
  { id: 'genres', label: 'Genres' },
  { id: 'packs', label: 'Packs' },
  { id: 'flip-sources', label: 'Flip sources' },
  { id: 'matches', label: 'Matches' },
  { id: 'rules', label: 'Rules' },
  { id: 'tournaments', label: 'Tournaments' },
  { id: 'playlists', label: 'Playlists' },
  { id: 'reports', label: 'Reports' },
  { id: 'support', label: 'Support' },
];

function AdminPanel() {
  const { data: session } = useSession();
  const { tab } = useSearch({ from: '/admin/' });
  const navigate = useNavigate({ from: '/admin/' });

  if (session?.role !== 'admin') {
    return (
      <section className="mx-auto max-w-2xl px-4 py-20 text-center">
        <h2 className="text-2xl font-bold">Admins only</h2>
      </section>
    );
  }

  function setTab(next: TabId) {
    navigate({ search: { tab: next } });
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-12 space-y-8">
      <header>
        <h2 className="text-3xl font-bold">Admin</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Manage users, genres, sample packs, flip sources, and matches.
        </p>
      </header>

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab panels */}
      <div>
        {tab === 'overview' && <OverviewSection />}
        {tab === 'users' && <UsersSection />}
        {tab === 'genres' && <GenresSection />}
        {tab === 'packs' && <PacksSection />}
        {tab === 'flip-sources' && <FlipSourcesSection />}
        {tab === 'matches' && <MatchesSection />}
        {tab === 'rules' && <RulesSection />}
        {tab === 'tournaments' && <TournamentsSection />}
        {tab === 'playlists' && <PlaylistsSection />}
        {tab === 'reports' && <ReportsSection />}
        {tab === 'support' && <SupportSection />}
      </div>
    </section>
  );
}
